import { cx } from '@utils/cx'
import { useEffect, useRef } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { ShareIcon } from '@components/interface/Icons'
import styles from './EndGameModal.module.css'
import baseStyles from '@styles/modalBase.module.css'

/**
 * EndGameModal pure UI, agnostic of game.
 * Receives handlers and flags via props. Does not read game contexts.
 */

export default function EndGameModal ({
  onPlayAgain,
  onGoHome,
  onShare,
  shareVisible = false,
  isSharing = false,
  shareError = ''
}) {
  const { t } = useLanguage()
  const messageRef = useRef(null)
  const modalRef = useRef(null)
  useFocusTrap(modalRef)

  useEffect(() => {
    if (messageRef.current) messageRef.current.focus()
  }, [])

  return (
    <div
      className={styles.endGameModal}
      role='dialog'
      aria-modal='true'
      aria-labelledby='end-message'
      ref={modalRef}
    >
      <div className={baseStyles.bottomHandle} />
      <h2
        id='end-message'
        className={styles.message}
        ref={messageRef}
        tabIndex='-1'
      >
        {t.shared.endGame.question}
      </h2>
      <div className={styles.buttons}>
        {shareVisible && typeof onShare === 'function' && (
          <button
            className={cx(styles.button, styles.buttonShare)}
            onClick={onShare}
            disabled={isSharing}
            aria-label={t.shared.access.share?.button || 'Share my result'}
          >
            <ShareIcon size={18} stroke={1.5} aria-hidden='true' />
            {isSharing ? (t.shared.share?.generating || 'Generating...') : (t.shared.share?.button || 'Share my result')}
          </button>
        )}

        <button
          className={styles.button}
          onClick={onPlayAgain}
          aria-label={t.shared.access.game.restart}
        >
          {t.shared.endGame.restart}
        </button>

        <button
          className={styles.button}
          onClick={onGoHome}
          aria-label={t.shared.access.game.reset}
        >
          {t.shared.endGame.reset}
        </button>
      </div>

      {Boolean(shareError) && (
        <div className={styles.error} role='alert' aria-live='assertive'>
          {shareError}
        </div>
      )}
    </div>
  )
}
